import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import NormalField from './normal-field';
import { formatPrimaryCarePostCode } from '../../helpers/primaryCareAddress';

const MANUAL_ENTRY_OPTION = {
  label: 'Can\'t find your surgery? Enter address manually',
  isManualEntry: true,
};
const LOOKUP_ERROR_FALLBACK_MESSAGE = 'There was an error finding your surgery.';
const SELECT_SURGERY_MESSAGE = 'Please choose your surgery from the list below:';
const parseLookupResponse = async (response) => {
  if (typeof response.clone === 'function') {
    try {
      const responseText = await response.clone().text();

      if (!responseText) {
        return null;
      }

      try {
        return JSON.parse(responseText);
      } catch (error) {
        return responseText.trim();
      }
    } catch (error) {
      // Fall back to the response helpers below when clone/text parsing is unavailable.
    }
  }

  if (typeof response.json === 'function') {
    try {
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  return null;
};

const getLookupErrorDetail = (data) => {
  if (!data) {
    return '';
  }

  if (typeof data === 'string') {
    return data.trim();
  }

  if (typeof data.message === 'string') {
    return data.message.trim();
  }

  if (typeof data.error === 'string') {
    return data.error.trim();
  }

  if (typeof data.payload?.message === 'string') {
    return data.payload.message.trim();
  }

  if (Array.isArray(data.errors)) {
    const firstError = data.errors.find((item) => typeof item === 'string' || typeof item?.message === 'string');

    if (typeof firstError === 'string') {
      return firstError.trim();
    }

    if (typeof firstError?.message === 'string') {
      return firstError.message.trim();
    }
  }

  return '';
};

const summarizeLookupFailure = ({ response, data, error }) => {
  if (response) {
    const statusSummary = `status ${response.status}${response.statusText ? ` ${response.statusText}` : ''}`;
    const errorDetail = getLookupErrorDetail(data);

    return errorDetail ? `${statusSummary} - ${errorDetail}` : statusSummary;
  }

  if (typeof error === 'string') {
    return error;
  }

  if (error?.message) {
    return error.message;
  }

  return LOOKUP_ERROR_FALLBACK_MESSAGE;
};
const summarizeLookupSuccess = ({ response, matches }) => {
  const statusSummary = `status ${response.status}${response.statusText ? ` ${response.statusText}` : ''}`;
  const matchSummary = `${matches.length} ${matches.length === 1 ? 'match' : 'matches'}`;

  return `${statusSummary} - ${matchSummary}`;
};

const PrimaryCareAddressLookup = ({ register, errors, isSubmitted, setValue, clearErrors }) => {
  const apiUrl = process.env.REACT_APP_PRIMARY_CARE_LOOKUP;
  const [lookupPostCode, setLookupPostCode] = useState('');
  const [postCode, setPostCode] = useState('');
  const [address1, setAddress1] = useState('');
  const [odsCode, setOdsCode] = useState('');
  const [address2, setAddress2] = useState('');
  const [city, setCity] = useState('');
  const [matches, setMatches] = useState([]);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [selectedLookupSnapshot, setSelectedLookupSnapshot] = useState(null);
  const [entryMethod, setEntryMethod] = useState(null);
  const [lookupMessage, setLookupMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const canLookUp = formatPrimaryCarePostCode(lookupPostCode).length > 0;

  useEffect(() => {
    register('nhsVerified');
    register('addressEntryMethod');
    register('odsCode');
    register('address1');
    register('address2');
    register('city');
    register('postCode');
  }, [register]);

  const showAddressFields = useMemo(
    () => (
      entryMethod === 'manual'
      || entryMethod === 'lookup'
      || !!isSubmitted
      || !!errors.address1
      || !!errors.address2
      || !!errors.city
      || !!errors.postCode
    ),
    [entryMethod, isSubmitted, errors.address1, errors.address2, errors.city, errors.postCode],
  );
  const requiredFieldFallbackErrors = {
    address1: isSubmitted && !String(address1 || '').trim() ? { message: 'Field Required' } : undefined,
    address2: isSubmitted && !String(address2 || '').trim() ? { message: 'Field Required' } : undefined,
    city: isSubmitted && !String(city || '').trim() ? { message: 'Field Required' } : undefined,
    postCode: isSubmitted && !String(postCode || '').trim() ? { message: 'Field Required' } : undefined,
  };

  const updateFormValue = (field, value) => {
    setValue(field, value, { shouldDirty: true, shouldTouch: true, shouldValidate: false });
  };

  const updateDerivedFormValue = (field, value) => {
    setValue(field, value, { shouldDirty: false, shouldTouch: false, shouldValidate: false });
  };

  const buildLookupSnapshot = (match) => ({
    postCode: formatPrimaryCarePostCode(lookupPostCode),
    address1: match.address1 || '',
    odsCode: match.odsCode || '',
    address2: match.address2 || '',
    city: match.city || '',
  });

  const matchesLookupSnapshot = (nextFields, snapshot = selectedLookupSnapshot) => {
    if (!snapshot) {
      return false;
    }

    return ['postCode', 'address1', 'odsCode', 'address2', 'city'].every((field) => nextFields[field] === snapshot[field]);
  };

  const syncNhsVerified = (overrides = {}, nextEntryMethod = entryMethod, nextSnapshot = selectedLookupSnapshot) => {
    if (nextEntryMethod === 'manual') {
      updateDerivedFormValue('nhsVerified', false);
      return;
    }

    if (nextEntryMethod === 'lookup' && nextSnapshot) {
      updateDerivedFormValue('nhsVerified', matchesLookupSnapshot({
        postCode,
        address1,
        odsCode,
        address2,
        city,
        ...overrides,
      }, nextSnapshot));
      return;
    }

    updateDerivedFormValue('nhsVerified', undefined);
  };

  const updateAddressFields = (nextAddress) => {
    if (typeof nextAddress.postCode === 'string') {
      setPostCode(nextAddress.postCode);
      updateFormValue('postCode', nextAddress.postCode);
    }
    setAddress1(nextAddress.address1);
    setOdsCode(nextAddress.odsCode || '');
    setAddress2(nextAddress.address2);
    setCity(nextAddress.city);
    updateFormValue('address1', nextAddress.address1);
    updateFormValue('odsCode', nextAddress.odsCode || '');
    updateFormValue('address2', nextAddress.address2);
    updateFormValue('city', nextAddress.city);
  };

  const clearResolvedAddress = () => {
    setAddress1('');
    setOdsCode('');
    setAddress2('');
    setCity('');
    setSelectedLookupSnapshot(null);
    updateFormValue('address1', '');
    updateFormValue('odsCode', '');
    updateFormValue('address2', '');
    updateFormValue('city', '');
    updateDerivedFormValue('nhsVerified', undefined);
    updateFormValue('addressEntryMethod', undefined);
  };

  const resetLookupState = () => {
    setMatches([]);
    setSelectedMatch(null);
    setEntryMethod(null);
    setLookupMessage('');
    clearResolvedAddress();
    clearErrors(['address1', 'address2', 'city', 'addressEntryMethod']);
  };

  const enableManualEntry = (message = '') => {
    setMatches([]);
    setSelectedMatch(null);
    setSelectedLookupSnapshot(null);
    setEntryMethod('manual');
    setLookupMessage(message);
    updateDerivedFormValue('nhsVerified', false);
    updateFormValue('addressEntryMethod', 'manual');
    clearErrors(['addressEntryMethod']);
  };

  const applyLookupMatch = (match) => {
    const nextSnapshot = buildLookupSnapshot(match);
    setSelectedMatch(match);
    setSelectedLookupSnapshot(nextSnapshot);
    setEntryMethod('lookup');
    setLookupMessage('');
    updateAddressFields(nextSnapshot);
    updateDerivedFormValue('nhsVerified', true);
    updateFormValue('addressEntryMethod', 'lookup');
    clearErrors(['address1', 'address2', 'city', 'addressEntryMethod']);
  };

  const applyManualMatch = (match) => {
    setSelectedMatch(match);
    setSelectedLookupSnapshot(null);
    setEntryMethod('manual');
    setLookupMessage('');
    updateAddressFields({
      address1: '',
      odsCode: '',
      address2: '',
      city: '',
    });
    updateDerivedFormValue('nhsVerified', false);
    updateFormValue('addressEntryMethod', 'manual');
    clearErrors(['address1', 'address2', 'city', 'addressEntryMethod']);
  };

  const handleLookUp = async () => {
    if (!canLookUp || isLoading) {
      return;
    }

    const formattedPostCode = formatPrimaryCarePostCode(lookupPostCode);
    setPostCode(formattedPostCode);
    updateFormValue('postCode', formattedPostCode);

    if (!apiUrl) {
      enableManualEntry('No surgery was found at this postcode. Please try again or enter your details manually:');
      return;
    }

    setIsLoading(true);
    setLookupMessage('');
    setSelectedMatch(null);
    setMatches([]);
    clearResolvedAddress();

    try {
      const response = await fetch(`${apiUrl}${encodeURIComponent(formattedPostCode)}`, {
        method: 'GET',
        headers: {
          'x-api-key': process.env.REACT_APP_API_KEY,
        },
      });
      const data = await parseLookupResponse(response);

      if (!response.ok) {
        throw new Error(summarizeLookupFailure({ response, data }));
      }

      const nextMatches = Array.isArray(data?.payload?.matches) ? data.payload.matches : [];
      console.info('Primary care NHS lookup succeeded:', summarizeLookupSuccess({ response, matches: nextMatches }));

      if (nextMatches.length === 0) {
        enableManualEntry('No surgery was found at this postcode. Please try again or enter your details manually:');
        return;
      }

      setMatches([...nextMatches, MANUAL_ENTRY_OPTION]);
      setEntryMethod(null);
      setLookupMessage(SELECT_SURGERY_MESSAGE);
      updateDerivedFormValue('nhsVerified', undefined);
      updateFormValue('addressEntryMethod', undefined);
      clearErrors(['address1', 'address2', 'city', 'addressEntryMethod']);
    } catch (error) {
      console.error('Primary care NHS lookup failed:', summarizeLookupFailure({ error }));
      enableManualEntry('No surgery was found at this postcode. Please try again or enter your details manually:');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLookupPostcodeChange = (event) => {
    const nextPostCode = event.target.value.toUpperCase();
    setLookupPostCode(nextPostCode);

    if (entryMethod || matches.length > 0 || selectedMatch || lookupMessage) {
      resetLookupState();
    }
  };

  const handleLookupPostcodeKeyDown = (event) => {
    if (event.key !== 'Enter') {
      return;
    }

    event.preventDefault();

    if (canLookUp && !isLoading) {
      handleLookUp();
    }
  };

  const handlePostCodeChange = (event) => {
    const nextPostCode = event.target.value.toUpperCase();
    setPostCode(nextPostCode);
    updateFormValue('postCode', nextPostCode);
    syncNhsVerified({ postCode: nextPostCode });
  };

  const handleAddress1Change = (event) => {
    const nextAddress1 = event.target.value;
    setAddress1(nextAddress1);
    updateFormValue('address1', nextAddress1);
    syncNhsVerified({ address1: nextAddress1 });
  };

  const handleAddress2Change = (event) => {
    const nextAddress2 = event.target.value;
    setAddress2(nextAddress2);
    updateFormValue('address2', nextAddress2);
    syncNhsVerified({ address2: nextAddress2 });
  };

  const handleOdsCodeChange = (event) => {
    const nextOdsCode = event.target.value;
    setOdsCode(nextOdsCode);
    updateFormValue('odsCode', nextOdsCode);
    syncNhsVerified({ odsCode: nextOdsCode });
  };

  const handleCityChange = (event) => {
    const nextCity = event.target.value;
    setCity(nextCity);
    updateFormValue('city', nextCity);
    syncNhsVerified({ city: nextCity });
  };

  const handleSelectMatch = (event, value) => {
    if (value) {
      if (value.isManualEntry) {
        applyManualMatch(value);
        return;
      }

      applyLookupMatch(value);
    }
  };

  return (
    <Box className="form-group">
      <Box className="form-group horizontal-form mobile-row" sx={{ marginTop: '24px', alignItems: 'center' }}>
        <NormalField
          id="primary-care-lookup-postcode"
          label="Surgery/Practice Postcode *"
          placeholder="Surgery/Practice Postcode"
          value={lookupPostCode}
          onChange={handleLookupPostcodeChange}
          onKeyDown={handleLookupPostcodeKeyDown}
        />
        <Button
          className="rounded-btn text-white"
          variant="contained"
          color="darkBlue"
          sx={{ paddingX: '32px', paddingY: '12px' }}
          onClick={handleLookUp}
          disabled={!canLookUp || isLoading}
          type="button"
        >
          {isLoading ? 'Finding...' : 'Find address'}
        </Button>
      </Box>
      {lookupMessage ? (
        <Typography
          className={lookupMessage === SELECT_SURGERY_MESSAGE ? 'secondary-text' : 'danger-txt'}
          sx={{ marginTop: '8px', textAlign: 'left' }}
        >
          {lookupMessage}
        </Typography>
      ) : null}
      {errors.addressEntryMethod ? (
        <Typography className="danger-txt" sx={{ marginTop: '8px', textAlign: 'left' }}>
          {errors.addressEntryMethod.message}
        </Typography>
      ) : null}
      {matches.length > 1 ? (
        <Autocomplete
          disablePortal
          id="primary-care-address-options"
          options={matches}
          value={selectedMatch}
          ListboxProps={{
            sx: {
              '& .MuiAutocomplete-option:not(:last-of-type)': {
                borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
              },
            },
          }}
          onChange={handleSelectMatch}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.label === value.label}
          renderInput={(params) => (
            <TextField
              {...params}
              InputLabelProps={{ shrink: true, sx: { top: '-18px', left: '-13px' }, disableAnimation: true }}
              sx={{ width: '100%', '& legend': { display: 'none' }, marginTop: '16px' }}
              label="Organisation *"
              placeholder="Select organisation"
            />
          )}
        />
      ) : null}
      {showAddressFields ? (
        <Box sx={{ marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <NormalField
            id="primary-care-odsCode"
            label="ODS Code"
            placeholder="Enter ODS code (optional)"
            register={register('odsCode')}
            value={odsCode}
            errors={errors.odsCode}
            onChange={handleOdsCodeChange}
          />
          <NormalField
            id="primary-care-address1"
            label="Surgery name *"
            placeholder="Enter surgery name"
            register={register('address1')}
            value={address1}
            errors={errors.address1 || requiredFieldFallbackErrors.address1}
            onChange={handleAddress1Change}
          />
          <NormalField
            id="primary-care-address2"
            label="Address line *"
            placeholder="Enter address line"
            register={register('address2')}
            value={address2}
            errors={errors.address2 || requiredFieldFallbackErrors.address2}
            onChange={handleAddress2Change}
          />
          <NormalField
            id="primary-care-city"
            label="Town or City *"
            placeholder="Enter town or city"
            register={register('city')}
            value={city}
            errors={errors.city || requiredFieldFallbackErrors.city}
            onChange={handleCityChange}
          />
          <NormalField
            id="primary-care-postCode"
            label="Post code *"
            placeholder="Enter post code"
            register={register('postCode')}
            value={postCode}
            errors={errors.postCode || requiredFieldFallbackErrors.postCode}
            onChange={handlePostCodeChange}
          />
        </Box>
      ) : null}
    </Box>
  );
};

export default PrimaryCareAddressLookup;
